/**
 * Prompt Library Service
 * Handles all user prompt-related API calls to the FastAPI backend.
 */

import { apiRequest } from '@/lib/api-client'
import { logError, logInfo } from '@/lib/logger'

/**
 * User prompt interface matching backend schema.
 */
export interface UserPrompt {
  id: string
  user_id: string
  title: string
  description?: string | null
  prompt_text: string
  is_shared: boolean
  shared_with?: string[] | null
  sort_order: number
  created_at: string
  updated_at: string
}

/**
 * Prompt create payload.
 */
export interface PromptCreatePayload {
  title: string
  description?: string | null
  prompt_text: string
}

/**
 * Prompt update payload (partial).
 */
export type PromptUpdatePayload = Partial<PromptCreatePayload>

/**
 * Type for creating a new prompt (without DB-generated fields)
 */
export type CreatePromptInput = {
  title: string
  description?: string
  prompt_text: string
}

/**
 * Type for updating a prompt (partial fields)
 */
export type UpdatePromptInput = Partial<Pick<UserPrompt, 'title' | 'description' | 'prompt_text'>>

/**
 * Response type for prompt list endpoints.
 */
interface PromptListResponse {
  prompts: UserPrompt[]
  total: number
}

/**
 * Response type for prompt delete endpoint.
 */
interface PromptDeleteResponse {
  deleted_id: string
  message: string
}

/**
 * Share response from backend.
 */
interface PromptShareResponse {
  shared_count: number
  message: string
}

/**
 * Get all prompts for the current authenticated user
 */
export async function getPrompts(): Promise<UserPrompt[]> {
  try {
    const response = await apiRequest<PromptListResponse>('/user-prompts')
    logInfo({ context: 'PromptLibraryService' }, `Loaded ${response?.prompts.length ?? 0} prompts`)
    return response?.prompts ?? []
  } catch (error) {
    logError(error as Error, { context: 'PromptLibraryService', method: 'getPrompts' })
    return []
  }
}

/**
 * Create a new prompt for the current authenticated user
 */
export async function createPrompt(input: CreatePromptInput): Promise<UserPrompt | null> {
  const payload: PromptCreatePayload = {
    title: input.title,
    description: input.description ?? null,
    prompt_text: input.prompt_text,
  }
  try {
    const created = await apiRequest<UserPrompt>('/user-prompts', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
    if (created) {
      logInfo({ context: 'PromptLibraryService' }, `Created prompt: ${created.title}`)
    }
    return created
  } catch (error) {
    logError(error as Error, { context: 'PromptLibraryService', method: 'createPrompt' })
    throw error
  }
}

/**
 * Update an existing prompt
 * Only title, description, and prompt_text can be updated
 */
export async function updatePrompt(promptId: string, updates: UpdatePromptInput): Promise<UserPrompt | null> {
  try {
    const updated = await apiRequest<UserPrompt>(`/user-prompts/${promptId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    })
    if (updated) {
      logInfo({ context: 'PromptLibraryService' }, `Updated prompt: ${updated.title}`)
    }
    return updated
  } catch (error) {
    logError(error as Error, { context: 'PromptLibraryService', method: 'updatePrompt', promptId })
    throw error
  }
}

/**
 * Delete a prompt
 */
export async function deletePrompt(promptId: string): Promise<boolean> {
  try {
    const response = await apiRequest<PromptDeleteResponse>(`/user-prompts/${promptId}`, {
      method: 'DELETE',
    })
    if (response) {
      logInfo({ context: 'PromptLibraryService' }, `Deleted prompt: ${response.deleted_id}`)
      return true
    }
    return false
  } catch (error) {
    logError(error as Error, { context: 'PromptLibraryService', method: 'deletePrompt', promptId })
    throw error
  }
}

/**
 * Get a single prompt by ID
 */
export async function getPromptById(promptId: string): Promise<UserPrompt | null> {
  try {
    return await apiRequest<UserPrompt>(`/user-prompts/${promptId}`)
  } catch (error) {
    logError(error as Error, { context: 'PromptLibraryService', method: 'getPromptById', promptId })
    return null
  }
}

/**
 * Reorder prompts by providing IDs in desired display order.
 */
export async function reorderPrompts(orderedIds: string[]): Promise<UserPrompt[]> {
  try {
    const response = await apiRequest<PromptListResponse>('/user-prompts/reorder', {
      method: 'PUT',
      body: JSON.stringify({ ordered_ids: orderedIds }),
    })
    logInfo({ context: 'PromptLibraryService' }, `Reordered ${orderedIds.length} prompts`)
    return response?.prompts ?? []
  } catch (error) {
    logError(error as Error, { context: 'PromptLibraryService', method: 'reorderPrompts' })
    throw error
  }
}

/**
 * Share a prompt with other users (creates independent copies)
 */
export async function sharePrompt(promptId: string, targetUserIds: string[]): Promise<number> {
  try {
    const response = await apiRequest<PromptShareResponse>(`/user-prompts/${promptId}/share`, {
      method: 'POST',
      body: JSON.stringify({ target_user_ids: targetUserIds }),
    })
    if (response) {
      logInfo({ context: 'PromptLibraryService' }, `Shared prompt with ${response.shared_count} users`)
      return response.shared_count
    }
    return 0
  } catch (error) {
    logError(error as Error, { context: 'PromptLibraryService', method: 'sharePrompt', promptId })
    throw error
  }
}
